import api, { type ApiError } from '@lib/axios';
import type { AuthTokens, Organization, Subscription, User } from '@witzy/shared';


// --- Request shapes ---------------------------------------------------------

export interface RegisterInput {
  organization_name: string;
  company_name?: string;
  admin_email: string;
  admin_password: string;
  first_name: string;
  last_name?: string;
  admin_phone?: string;
  industry?: string;
  website?: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

// --- Response shapes --------------------------------------------------------

export interface AuthResponse {
  user: User;
  organization: Organization | null;
  tokens: AuthTokens;
}

export interface MeResponse {
  user: User;
  organization: Organization | null;
  subscription: Subscription | null;
}

// --- API calls --------------------------------------------------------------

export async function register(input: RegisterInput): Promise<AuthResponse> {
  const { data } = await api.post<{ data: AuthResponse }>('/auth/register', input);
  return data.data;
}

export async function login(input: LoginInput): Promise<AuthResponse> {
  const { data } = await api.post<{ data: AuthResponse }>('/auth/login', input);
  return data.data;
}

export async function refreshTokens(refreshToken: string): Promise<AuthTokens> {
  const { data } = await api.post<{ data: AuthTokens }>('/auth/refresh', {
    refresh_token: refreshToken,
  });
  return data.data;
}

export async function logout(): Promise<void> {
  try {
    await api.post('/auth/logout');
  } catch {
    // Best-effort; clearAuth handles the client state regardless.
  }
}

export async function forgotPassword(email: string): Promise<void> {
  await api.post('/auth/forgot-password', { email });
}

export async function resetPassword(token: string, password: string): Promise<void> {
  await api.post('/auth/reset-password', { token, password });
}

export async function verifyEmail(token: string): Promise<void> {
  await api.post('/auth/verify-email', { token });
}

/** Re-send the 6-digit verification code to the current (authenticated) user. */
export async function resendVerification(): Promise<void> {
  await api.post('/auth/resend-verification', undefined, { skipProjectId: true });
}

export async function getMe(): Promise<MeResponse> {
  // /auth/me is JWT-scoped; the project interceptor would add a noisy
  // ?project_id=… query that the endpoint ignores.
  const { data } = await api.get<{ data: MeResponse }>('/auth/me', {
    skipProjectId: true,
  });
  return data.data;
}

/** Type guard for our normalised API errors. */
export function isApiError(err: unknown): err is ApiError {
  return typeof err === 'object' && err !== null && 'statusCode' in err && 'message' in err;
}
